import { useState } from "react";
import { PageTemplate } from "@/views/PageTemplate";
import { SwapIcon } from "@/components/icons";
import { TokenOption } from "@/components/ui/types";
import usdcTokenImage from "@/assets/tokens/usdc.svg?url";
import piqueTokenImage from "@/assets/tokens/icon_i-onlight.svg?url";
import swapIcon from "@/assets/icons/swap.svg?url";

export function SwapPage() {
  // Define tokens
  const usdcToken: TokenOption = {
    id: "usdc",
    name: "USDC",
    icon: <img src={usdcTokenImage} alt='USDC' className='w-6 h-6 rounded-full' />,
  };

  const piqueToken: TokenOption = {
    id: "pique",
    name: "PIQUE",
    icon: <img src={piqueTokenImage} alt='PIQUE' className='w-6 h-6 rounded-full' />,
  };

  // State declarations
  const [inputAmount, setInputAmount] = useState("1,000.00");
  const [outputAmount] = useState("642.25");
  const [selectedInputToken] = useState<TokenOption>(usdcToken);
  const [selectedOutputToken] = useState<TokenOption>(piqueToken);

  const handleSwap = () => {
    console.log("Swap executed:", { inputAmount, outputAmount });
  };

  return (
    <PageTemplate title='Swap' subtitle='Exchange one token for another.' icon={<SwapIcon active={true} className='h-[16px]' />}>
      <div className='bg-(--light-gray) rounded-2xl p-6 mb-[48px]'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {/* Input Amount Section */}
          <div>
            <p className='text-(--gray-3) mb-2 text-sm text-left font-medium'>You will exchange...</p>
            <div className='flex items-center bg-white rounded-lg p-4 border border-gray-200'>
              <input
                type='text'
                value={inputAmount}
                onChange={(e) => setInputAmount(e.target.value)}
                className='flex-1 text-xl font-medium outline-none'
                placeholder='0.00'
              />
              <div className='flex items-center gap-2 ml-2 cursor-pointer'>
                <img src={usdcTokenImage} alt='USDC' className='w-6 h-6 rounded-full' />
                <span className='font-medium'>{selectedInputToken.name}</span>
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M4 6L8 10L12 6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </div>
            </div>
            <p className='text-(--gray-3) text-left text-sm mt-2'>Available to Swap: 15,687.05 USDC</p>
          </div>

          {/* Output Amount Section */}
          <div>
            <p className='text-(--gray-3) mb-2 text-sm text-left font-medium'>You will receive...</p>
            <div className='flex items-center bg-white rounded-lg p-4 border border-gray-200'>
              <div className='flex items-center gap-2 mr-2 cursor-pointer'>
                <img src={piqueTokenImage} alt='PIQUE' className='w-6 h-6 rounded-full' />
                <span className='font-medium'>{selectedOutputToken.name}</span>
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M4 6L8 10L12 6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </div>
              <input type='text' value={outputAmount} readOnly className='flex-1 text-xl font-medium outline-none text-right' placeholder='0.00' />
            </div>
            <p className='text-(--gray-3) text-left text-sm mt-2'>Current Balance: 0.00 PIQUE</p>
          </div>
        </div>
      </div>

      {/* Transaction Review */}
      <div className='mb-6'>
        <h3 className='font-medium text-sm mb-3 text-left'>Transaction Review</h3>
        <ul className='space-y-2'>
          <li className='flex items-start'>
            <svg className='w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
            <span className='text-sm'>You are exchanging 1,000 USDC tokens for 642.25 PIQUE tokens.</span>
          </li>
          <li className='flex items-start'>
            <svg className='w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
            <span className='text-sm'>Currently, 1 USDC is worth 0.64225 PIQUE.</span>
          </li>
          <li className='flex items-start'>
            <svg className='w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
            <span className='text-sm'>
              Based on the current{" "}
              <a href='#' className='text-blue-600 underline'>
                slippage settings
              </a>{" "}
              of 0.1%, the minimum you will receive is 641.60 PIQUE tokens.
            </span>
          </li>
        </ul>
      </div>

      {/* Swap Button */}
      <div>
        <button
          onClick={handleSwap}
          className='flex items-center justify-center gap-2 bg-blue-500 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-600 transition-colors w-auto'
        >
          Make the Swap
          <img src={swapIcon} alt='Swap' className='w-4 h-4' />
        </button>
      </div>
    </PageTemplate>
  );
}
